import React, { useState, useRef, useEffect } from 'react';

/* Champ d'adresse avec autocomplétion via la Base Adresse Nationale (BAN),
   API publique et gratuite du gouvernement : api-adresse.data.gouv.fr.
   Aucune clé requise. Remplace un <input> classique.
   onSelectCoords(lat, lng) est optionnel : renvoie les coordonnées GPS de
   l'adresse choisie (utilisées pour l'optimisation de tournée). */
export default function AddressAutocomplete({ value, onChange, placeholder, style, darkMode, onSelectCoords }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchSuggestions = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.length < 3) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5&autocomplete=1`);
        const data = await res.json();
        setSuggestions((data.features || []).map(f => ({ label: f.properties.label, lat: f.geometry?.coordinates?.[1], lng: f.geometry?.coordinates?.[0] })));
        setOpen(true);
        setHighlight(-1);
      } catch (e) { /* hors-ligne : on laisse la saisie libre */ }
    }, 250);
  };

  const handleChange = (e) => { onChange(e.target.value); fetchSuggestions(e.target.value); };
  const choose = (s) => { onChange(s.label); if (onSelectCoords && s.lat != null) onSelectCoords(s.lat, s.lng); setSuggestions([]); setOpen(false); };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); choose(suggestions[highlight]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={placeholder || 'Adresse'}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        autoComplete="off"
        style={style}
      />
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '12px', boxShadow: '0 12px 32px rgba(0,0,0,0.18)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', zIndex: 50, overflow: 'hidden' }}>
          {suggestions.map((s, i) => (
            <div
              key={i}
              onMouseDown={() => choose(s)}
              onMouseEnter={() => setHighlight(i)}
              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: darkMode ? '#e5e7eb' : '#374151', background: highlight === i ? (darkMode ? '#111827' : '#f8faff') : 'transparent', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span style={{ fontSize: '13px' }}>📍</span>{s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
